import axios from "axios";
import ENDPOINTS from "./endpoints";
import { ROLES } from "@/utils/constants";

let accessToken: string | null = null;
const tokenObservers: ((token: string | null) => void)[] = [];

export const registerTokenObserver = (
  observer: (token: string | null) => void,
) => {
  tokenObservers.push(observer);
  observer(accessToken);
};

export const getToken = async (): Promise<string | null> => {
  return accessToken;
};

export const setAccessToken = (token: string): void => {
  if (!token) {
    console.error("Invalid token provided to setAccessToken");
    return;
  }
  accessToken = token;
  tokenObservers.forEach((obs) => obs(token));
};

export const clearAccessToken = (): void => {
  accessToken = null;
  tokenObservers.forEach((obs) => obs(null));
};

// Helper function to check if user is authenticated
export const isAuthenticated = (): boolean => {
  return !!accessToken && !isTokenExpired(accessToken);
};

// Basic JWT expiration check
const isTokenExpired = (token: string): boolean => {
  try {
    // JWT tokens have 3 parts separated by dots
    const parts = token.split(".");
    if (parts.length !== 3) {
      // Not a JWT, assume it's valid for now
      return false;
    }

    // Decode the payload (second part)
    const payload = JSON.parse(atob(parts[1]));

    // Check if token has an expiration time
    if (payload.exp) {
      const currentTime = Math.floor(Date.now() / 1000);
      const isExpired = payload.exp < currentTime;

      if (isExpired) {
        console.warn("JWT token has expired");
      }

      return isExpired;
    }

    // No expiration time in token, assume it's valid
    return false;
  } catch (error) {
    console.warn("Could not decode token for expiration check:", error);
    // If we can't decode it, assume it's still valid
    return false;
  }
};

export const refreshAccessToken = async (): Promise<string | null> => {
  try {
    const api = axios.create({
      baseURL: ENDPOINTS.BASE_URL,
      withCredentials: true,
    });

    const response = await api.post(ENDPOINTS.AUTH.REFRESH_TOKEN, {
      role: ROLES.CONTRIBUTOR,
    });
    return response.data?.data?.token;
  } catch (error) {
    return null;
  }
};

interface JwtPayload {
  username?: string;
  user_id?: string | number;
  role_id?: number;
  role_name?: string;
  exp?: number;
  iat?: number;
  [key: string]: unknown;
}

export const decodeToken = (): JwtPayload | null => {
  try {
    const token = accessToken;
    if (!token) return null;

    const parts = token.split(".");
    if (parts.length !== 3) return null;

    const payload = JSON.parse(atob(parts[1]));
    return payload;
  } catch (error) {
    console.error("Failed to decode token:", error);
    return null;
  }
};
